import Dexie from 'dexie';
import { LogLevel } from '../Logging/LogLevel';
import { ILogStore } from '../Logging/ILogStore';
import { ILogMechanism } from '../Logging/ILogMechanism';
import {
    SignageLogItem,
    SignageLogItemSchema,
    SignagePlayEventSchema,
} from '@reachmedianetwork/signage-abstractions';
import { NamedEventCode } from '@reachmedianetwork/signage-abstractions';
import { logRelay } from '../MessagingClient/instance';
import AutoResetEvent from '../Utility/AutoResetEvent';
import { asyncTimeout } from '../Utility/AsyncTimeout';
import { z } from 'zod';

type SignagePlayEventData = z.infer<typeof SignagePlayEventSchema>;
interface ProofOfPlayRecord {
    id: string;
    level: LogLevel;
    message: string;
    code: string;
    data: SignagePlayEventData;
    category: string;
    subcategory: string;
    time: number;
    priority: number;
}

class ProofOfPlayDatabase extends Dexie {
    proofOfPlay!: Dexie.Table<ProofOfPlayRecord, string>;

    constructor() {
        super('REACH_Signage_ProofOfPlay');
        this.version(1).stores({
            proofOfPlay: 'id, level, message, code, data, category, subcategory, time, priority',
        });
    }
}

export class DexieProofOfPlayLogForwarder implements ILogStore, ILogMechanism {
    private database: ProofOfPlayDatabase;
    private dbIsReady: Promise<Dexie>;
    private readonly logsAvailableEvent = new AutoResetEvent();

    constructor() {
        this.database = new ProofOfPlayDatabase();
        this.dbIsReady = this.database.open();

        // Start the log processing routine
        void this.logProcessingRoutine();
    }

    private async logProcessingRoutine(): Promise<void> {
        for (;;) {
            // Wait for the event to be signalled
            if (await this.logsAvailableEvent.waitOne(60000)) {
                // Wait 100 ms to see if more logs arrive
                await asyncTimeout(100);
            }

            // Attempt to relay available logs regardless of whether we were signalled
            try {
                await this.relayAllAvailableLogItems();
            } catch (e) {
                console.error('failed to send logs to Mercury FX', e);
            }
        }
    }

    private async relayAllAvailableLogItems(): Promise<void> {
        const theLogRelay = logRelay();
        if (!theLogRelay.isConnected || !theLogRelay.canRelayLogs) {
            return;
        }

        let lastLogCount: number;
        do {
            const logs = await this.getOldestLogs(10);
            lastLogCount = logs.length;

            if (lastLogCount > 0) {
                await theLogRelay.processLogs(logs);
            }

            await this.deleteLogs(logs);
        } while (lastLogCount === 10);
    }

    public NamedEvent = (
        code: NamedEventCode,
        message: string,
        source: string,
        data: unknown
    ): void => {
        if (code === NamedEventCode.SIGNAGE_PLAY_EVENT) {
            // Store POP in dexie db store
            void this.storeLog({
                level: LogLevel.INFO,
                message,
                code: code.toString(),
                source,
                data,
            });
        }
    };

    // Do nothing, as per requirements, only handle SIGNAGE_PLAY_EVENT via NamedEvent
    public LogMessage(): void {
        return;
    }

    private async storeLog(params: {
        level: LogLevel;
        message: string;
        code: string;
        source: string;
        data: unknown;
    }): Promise<void> {
        await this.dbIsReady;

        const signagePlayEventData = SignagePlayEventSchema.parse(params.data);
        const record: ProofOfPlayRecord = {
            id: this.generateGuid(),
            level: params.level,
            message: params.message,
            code: params.code,
            data: signagePlayEventData,
            category: 'Presentation',
            subcategory: 'POP', // Or extract from source like in REACHPlayerLogForwarderMechanism
            time: Date.now(),
            priority: 0, // Adjust as needed
        };

        await this.database.proofOfPlay.add(record);

        // Signal that logs are available
        this.logsAvailableEvent.set();
    }

    private generateGuid(): string {
        // Simple GUID generator, similar to C# code mentioned
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    public async getOldestLogs(limit: number): Promise<SignageLogItem[]> {
        await this.dbIsReady;

        const records = await this.database.proofOfPlay.orderBy('time').limit(limit).toArray();

        return records.map((record) => {
            return SignageLogItemSchema.parse(record);
        });
    }

    public async deleteLogs(logs: SignageLogItem[]): Promise<void> {
        await this.deleteLogsById(logs.map((log) => log.id));
    }

    public async deleteLogsById(logIds: string[]): Promise<void> {
        await this.dbIsReady;
        await this.database.proofOfPlay.where('id').anyOf(logIds).delete();
    }
}
